async function boundedBytes(response, maxBytes) {
  const declared = response.headers?.get('content-length');
  if (declared && (!/^\d+$/.test(declared) || Number(declared) > maxBytes)) throw new Error('Northstar response exceeds its size limit.');
  if (!response.body?.getReader) throw new Error('This browser cannot safely receive the Northstar package.');
  const reader = response.body.getReader(), chunks = []; let length = 0;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      length += value.byteLength;
      if (length > maxBytes) throw new Error('Northstar response exceeds its size limit.');
      chunks.push(value);
    }
  } catch (error) { try { await reader.cancel(); } catch {} throw error; }
  finally { reader.releaseLock(); }
  const bytes = new Uint8Array(length); let offset = 0;
  for (const chunk of chunks) { bytes.set(chunk, offset); offset += chunk.length; }
  return bytes;
}

async function readNorthstar(url, options, parse) {
  const { signal, ...requestOptions } = options;
  if (signal?.aborted) throw new DOMException('Northstar request cancelled.', 'AbortError');
  const controller = new AbortController();
  let rejectStopped;
  const stopped = new Promise((resolve, reject) => { rejectStopped = reject; });
  const interrupt = (message, name) => {
    controller.abort(); rejectStopped(Object.assign(new Error(message), { name }));
  };
  const cancel = () => interrupt('Northstar request cancelled.', 'AbortError');
  signal?.addEventListener('abort', cancel, { once: true });
  const timer = setTimeout(() => interrupt('Northstar request timed out after 30 seconds.', 'TimeoutError'), 30000);
  try {
    const request = (async () => {
      const response = await fetch(url, { ...requestOptions, cache: 'no-store', credentials: 'omit', signal: controller.signal });
      return parse(response);
    })();
    return await Promise.race([request, stopped]);
  } catch (error) { controller.abort(); throw error; }
  finally { clearTimeout(timer); signal?.removeEventListener('abort', cancel); }
}

export async function readNorthstarJson(url, options = {}) {
  const { maxBytes, ...requestOptions } = options;
  return readNorthstar(url, requestOptions, async response => {
    const body = maxBytes ? JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(await boundedBytes(response, maxBytes))) : await response.json();
    if (!response.ok || body?.ok === false) {
      const diagnostic = body?.storageDiagnostic;
      const stages = ['UPLOAD_REQUEST', 'UPLOAD_RECEIPT', 'SIGNED_URL', 'READBACK_RESPONSE', 'READBACK_BODY', 'SOURCE_READ'];
      const reasons = ['PROVIDER_REJECTED', 'FORMAT_REJECTED', 'STREAM_ERROR', 'URL_BUILD', 'RECEIPT_URL', 'RECEIPT_ID',
        'RECEIPT_RESOURCE_TYPE', 'RECEIPT_DELIVERY_TYPE', 'RECEIPT_SIZE', 'RECEIPT_VERSION', 'HTTP_STATUS',
        'MISSING_BODY', 'LENGTH_HEADER', 'BODY_TRANSPORT', 'UNEXPECTED_FAILURE'];
      const status = Number.isInteger(diagnostic?.providerStatus) && diagnostic.providerStatus >= 100 && diagnostic.providerStatus <= 599
        ? `; HTTP ${diagnostic.providerStatus}` : '';
      const detail = stages.includes(diagnostic?.stage) && reasons.includes(diagnostic?.reason)
        ? ` Storage check: ${diagnostic.stage} / ${diagnostic.reason}${status}.` : '';
      throw new Error(`${body?.error || 'Northstar request failed.'}${detail}`);
    }
    if (!body || typeof body !== 'object' || Array.isArray(body)) throw new Error('Northstar returned an unreadable response.');
    return body;
  });
}

export async function readNorthstarBytes(url, options = {}) {
  const { maxBytes = 256 * 1024, ...requestOptions } = options;
  return readNorthstar(url, requestOptions, async response => {
    if (!response.ok) throw new Error('The protected Northstar asset is no longer available. Check the package again.');
    return boundedBytes(response, maxBytes);
  });
}
